<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import dealReportsAPI from 'dashboard/api/dealReports';
import { useCommercialGoalsStore } from 'dashboard/stores/commercialGoals';
import { useCommercialGoalFormat } from 'dashboard/components-next/CommercialGoals/useCommercialGoalFormat';
import GoalProgressBar from 'dashboard/components-next/CommercialGoals/GoalProgressBar.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import TableHeaderCell from './TableHeaderCell.vue';
import { formatCurrencyBRL, formatDuration } from './commercialFormatters';
import { barDataset, barOptions } from '../charts/chartDesign';
import { useChartTheme } from '../charts/useChartTheme';
import { findUserGoals } from './commercialGoalMatchers';

const props = defineProps({
  params: { type: Object, required: true },
});

const { t } = useI18n();
const goalsStore = useCommercialGoalsStore();
const { formatValue, formatType, formatPeriod } = useCommercialGoalFormat();
const rows = ref([]);
const isLoading = ref(true);
const hasError = ref(false);
const sortKey = ref('revenue');
const sortDesc = ref(true);
const theme = useChartTheme();

const fetchData = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const response = await dealReportsAPI.getSalesReps(props.params);
    rows.value = response.data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.params, fetchData, { immediate: true, deep: true });

const COLUMNS = [
  { key: 'user_name', labelKey: 'USER', tooltipKey: 'SALES_REP_USER' },
  { key: 'open_count', labelKey: 'OPEN_COUNT', tooltipKey: 'OPEN_COUNT' },
  { key: 'won_count', labelKey: 'WON_COUNT', tooltipKey: 'WON_COUNT' },
  { key: 'lost_count', labelKey: 'LOST_COUNT', tooltipKey: 'LOST_COUNT' },
  { key: 'revenue', labelKey: 'REVENUE', tooltipKey: 'REVENUE' },
  {
    key: 'average_ticket',
    labelKey: 'AVERAGE_TICKET',
    tooltipKey: 'AVERAGE_TICKET',
  },
  {
    key: 'conversion_rate',
    labelKey: 'CONVERSION_RATE',
    tooltipKey: 'CONVERSION_RATE',
  },
  {
    key: 'average_purchase_time_seconds',
    labelKey: 'AVERAGE_PURCHASE_TIME',
    tooltipKey: 'AVERAGE_PURCHASE_TIME',
  },
];

const sortedRows = computed(() =>
  [...rows.value].sort((a, b) => {
    const direction = sortDesc.value ? -1 : 1;
    const left = a[sortKey.value] ?? '';
    const right = b[sortKey.value] ?? '';
    if (left === right) return 0;
    return left > right ? direction : -direction;
  })
);

const toggleSort = key => {
  if (sortKey.value === key) {
    sortDesc.value = !sortDesc.value;
  } else {
    sortKey.value = key;
    sortDesc.value = true;
  }
};

const rankedByRevenue = computed(() =>
  [...rows.value].sort((a, b) => b.revenue - a.revenue)
);

const chartCollection = computed(() => ({
  labels: rankedByRevenue.value.map(
    row => row.user_name || t('DASHBOARD.COMERCIAL.SALES_REPS.UNASSIGNED')
  ),
  datasets: [
    barDataset({
      data: rankedByRevenue.value.map(row => row.revenue),
      label: t('DASHBOARD.COMERCIAL.SALES_REPS.REVENUE'),
    }),
  ],
}));

const chartOptions = computed(() =>
  barOptions({ theme: theme.value, formatTick: formatCurrencyBRL })
);

const chartHeight = computed(() => `${rows.value.length * 36 + 24}px`);

const formatCell = (column, row) => {
  const value = row[column.key];
  if (column.key === 'revenue' || column.key === 'average_ticket')
    return formatCurrencyBRL(value);
  if (column.key === 'average_purchase_time_seconds')
    return formatDuration(value);
  if (column.key === 'conversion_rate') return `${value}%`;
  if (column.key === 'user_name')
    return value || t('DASHBOARD.COMERCIAL.SALES_REPS.UNASSIGNED');
  return value;
};

// Metas individuais vigentes do vendedor da linha (a coluna não é ordenável).
const goalsFor = row => findUserGoals(goalsStore.progress, row.user_id);

const goalTooltip = goal =>
  t('DASHBOARD.COMERCIAL.GOALS.ROW_TOOLTIP', {
    type: formatType(goal),
    current: formatValue(goal.goalType, goal.progress.currentValue),
    target: formatValue(goal.goalType, goal.progress.targetValue),
    period: formatPeriod(goal),
  });
</script>

<template>
  <section class="border rounded-lg border-n-weak">
    <h2 class="p-3 font-medium text-n-slate-12">
      {{ t('DASHBOARD.COMERCIAL.SECTIONS.SALES_REPS') }}
    </h2>

    <p v-if="hasError" class="px-3 pb-3 text-sm text-n-ruby-9">
      {{ t('DASHBOARD.COMERCIAL.LOAD_ERROR') }}
      <button type="button" class="underline" @click="fetchData">
        {{ t('DASHBOARD.COMERCIAL.RETRY') }}
      </button>
    </p>
    <p v-else-if="isLoading" class="px-3 pb-3 text-sm text-n-slate-11">
      {{ t('DASHBOARD.COMERCIAL.LOADING') }}
    </p>
    <p v-else-if="!rows.length" class="px-3 pb-3 text-sm text-n-slate-10">
      {{ t('DASHBOARD.COMERCIAL.EMPTY_STATE') }}
    </p>
    <div
      v-else
      class="flex flex-col gap-4 px-3 pb-3 md:flex-row md:items-start"
    >
      <div class="flex-1 overflow-y-auto overflow-x-auto max-h-80">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-10">
              <th
                v-for="column in COLUMNS"
                :key="column.key"
                class="py-1 pr-3 cursor-pointer select-none"
                @click="toggleSort(column.key)"
              >
                <TableHeaderCell
                  :label="
                    t(`DASHBOARD.COMERCIAL.SALES_REPS.${column.labelKey}`)
                  "
                  :tooltip="
                    t(`DASHBOARD.COMERCIAL.TOOLTIPS.${column.tooltipKey}`)
                  "
                />
                <span v-if="sortKey === column.key">{{
                  sortDesc ? '▼' : '▲'
                }}</span>
              </th>
              <th class="py-1 pr-3 select-none">
                <TableHeaderCell
                  :label="t('DASHBOARD.COMERCIAL.GOALS.COLUMN')"
                  :tooltip="t('DASHBOARD.COMERCIAL.GOALS.COLUMN_TOOLTIP')"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in sortedRows"
              :key="row.user_id ?? 'unassigned'"
              class="border-t border-n-weak"
            >
              <td
                v-for="column in COLUMNS"
                :key="column.key"
                class="py-1 pr-3 text-n-slate-12"
              >
                {{ formatCell(column, row) }}
              </td>
              <td class="py-1 pr-3 min-w-36 text-n-slate-12">
                <div v-if="goalsFor(row).length" class="flex flex-col gap-2">
                  <GoalProgressBar
                    v-for="goal in goalsFor(row)"
                    :key="goal.id"
                    compact
                    :percent="goal.progress.progressPercent"
                    :state="goal.progress.state"
                    :tooltip="goalTooltip(goal)"
                  />
                </div>
                <template v-else>—</template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        class="hidden shrink-0 md:block md:w-64"
        :style="{ height: chartHeight }"
      >
        <BarChart :collection="chartCollection" :chart-options="chartOptions" />
      </div>
    </div>
  </section>
</template>
